import { peekActiveCompanyKey } from "utils/encryptForCompany";
import { useContext, useEffect, useState } from "react";
import { FC, Props } from "./typings";
import pluginService from "api/pluginService";
import { getKeys } from "utils/getKeys";
import { useAuth } from "react-oidc-context";
import { getUserId } from "utils/getUserId";
import { getUserIsActivated } from "utils/getUserIsActivated";
import PluginUpdate from "components/PluginUpdate";
import CPluginContext from "contexts/PluginContext";
import {
  resolvePluginConnectionKeys,
  setConnectedPluginCompanyKeyIdentity,
  companyKeyIdentity,
} from "utils/pluginConnection";
import { useNavigate } from "react-router-dom";

const whitelisted = [
  "recovery",
  "choose-workspace",
  "activation",
  "invitation",
];

export const PluginContext: FC<Props> = () => {
  const navigate = useNavigate();
  const userAgent = navigator.userAgent.toLowerCase();
  const url = window.location.href.split("/");
  const {
    data: pluginInformation,
    isFetching,
    isError,
  } = pluginService.useGetVersion();

  const { data: actualVersion, error: actualVersionError } =
    pluginService.useActualVersion();
  const { mutate: createConnection } = pluginService.useCreateConnection();
  const { mutate: setConnection } = pluginService.useSetConnection();
  const auth = useAuth();
  const userId = getUserId(auth.user!);
  const isActivated = getUserIsActivated(auth.user!);

  const { plugin, setPlugin } = useContext(CPluginContext);

  const [showPlugin, setShowPlugin] = useState(false);

  const connectPlugin = async () => {
    if (plugin.version !== "0") return;
    if (!auth.isAuthenticated || !isActivated) return;
    const keys = await getKeys(userId);
    if (!keys) return;

    const isWindows = userAgent.search("windows") !== -1;
    const isOS = userAgent.search("mac") !== -1;
    const isLinux = userAgent.search("linux") !== -1;
    let actual = true;

    if (actualVersionError) {
      actual = false;
      setShowPlugin(true);
    }

    if (!pluginInformation?.Version) return;
    if (!actualVersion && !actualVersionError) return;

    //@ts-ignore
    // pluginInformation.Version = '1';

    if (whitelisted.some((keyword) => url.includes(keyword))) {
      return;
    }

    if (
      isWindows &&
      pluginInformation?.Version !== actualVersion.Windows.Version
    ) {
      actual = false;
      setShowPlugin(true);
    }
    if (isOS && pluginInformation?.Version !== actualVersion.MacOS.Version) {
      setShowPlugin(true);
      actual = false;
    }
    if (isLinux && pluginInformation?.Version !== actualVersion.Linux.Version) {
      setShowPlugin(true);
      actual = false;
    }
    setPlugin({
      actual: actual,
      isMac: isOS,
      isWindows: isWindows,
      linkMac: actualVersion.MacOS.DownloadUrl,
      linkWindows: actualVersion.Windows.DownloadUrl,
      version: pluginInformation.Version ?? "0",
    });

    // check plugin version here

    // Klucz FIRMY (a nie intermediate) — patrz utils/pluginConnection.ts.
    let PrivateKey: string;
    let PublicKey: string;
    try {
      ({ PrivateKey, PublicKey } = await resolvePluginConnectionKeys(keys));
    } catch {
      // Brak materiału klucza — wtyczka zostaje niepołączona, reszta widoku działa.
      return;
    }

    createConnection(
      {
        AppId: "todo",
        Version: pluginInformation!.Version,
      },
      {
        onSuccess: (response) => {
          setConnection(
            {
              PrivateKey,
              PublicKey,
              //Token: response.data.Token,
              Token: response.data.AccessToken,
              RefreshToken: response.data.RefreshToken,
              Url: response.data.Url,
            },
            {
              onSuccess: () => {
                setConnectedPluginCompanyKeyIdentity(
                  companyKeyIdentity(peekActiveCompanyKey()),
                );
              },
            }
          );
        },
      }
    );
  };

  useEffect(() => {
    connectPlugin();
  }, [isFetching, isError, pluginInformation, auth, actualVersion, navigate]);

  return (
    <>
      {showPlugin && (
        <PluginUpdate
          linkMac={actualVersion.MacOS.DownloadUrl ?? ""}
          linkWindows={actualVersion.Windows.DownloadUrl ?? ""}
          isWindows={userAgent.search("windows") !== -1}
          isMac={userAgent.search("mac") !== -1}
          handleClose={() => setShowPlugin(false)}
        />
      )}
    </>
  );
};

export default PluginContext;
